/**
 * Detalle de una persona (doc §7 y ajustes 14/09 "Detalle"): tarjetita
 * Obligatorio/Opcional, plazo, señal de visualización real, cada paso del
 * contenido con fecha y respuestas (spec §6.3), export CSV del detalle. §8:
 * People puede desbloquear todos los niveles para pruebas.
 */
import { currentLocale } from '@/lib/locale';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Download, LockOpen } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { trpc } from '@/lib/trpc';
import type { LearningDashboardRow } from '@/../../shared/people-learning';
import { csvCell, fmtDate, fmtMs, plazoLabel, rowPlazo, statusLabel } from './learning-dash-utils';
import { EngagementDot } from './EngagementDot';

/**
 * §7: detalle de una persona — cada paso del contenido con fecha de completado y
 * las respuestas guardadas (spec §6.3), tiempo total y progreso; export CSV del
 * detalle. §8: People puede desbloquear todos los niveles para pruebas.
 */
export function UserDetailDialog({ email, row, canEdit, today, onClose }: {
  email: string;
  row: LearningDashboardRow | null;
  canEdit: boolean;
  today: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const detail = trpc.peopleLearning.getUserDetail.useQuery({ email }, { staleTime: 15_000 });
  const logExport = trpc.peopleLearning.logExport.useMutation();
  const setUnlock = trpc.peopleLearning.setUnlockAll.useMutation({
    onSuccess: () => { detail.refetch(); utils.peopleLearning.dashboard.invalidate(); toast.success(t('people-learning.dashboardtab.desbloqueo_actualizado', 'Desbloqueo actualizado')); },
    onError: (e) => toast.error(e.message),
  });
  const p = detail.data?.progress ?? null;
  const steps = detail.data?.steps ?? [];
  const done = steps.filter((s) => s.completedAt).length;
  const fmtAnswer = (v: unknown) => (Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? '✓' : '✗') : String(v ?? ''));

  const exportDetail = () => {
    if (logExport.isPending) return;
    const esc = csvCell;
    const header = ['Email', 'Nivel', 'Paso', 'Tipo', 'Opcional', 'Completado', 'Respuestas'];
    const lines = steps.map((s) =>
      [email, s.levelTitleEs, s.titleEs, s.type, s.optional ? 'sí' : 'no', s.completedAt ?? '',
        s.detail?.answers ? Object.entries(s.detail.answers).map(([k, v]) => `${k}=${fmtAnswer(v)}`).join(' | ') : (s.detail?.note ?? '')].map(esc).join(';')
    );
    const csv = '\ufeff' + header.map(esc).join(';') + '\n' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `learning-detalle-${email.split('@')[0]}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 400);
    logExport.mutate({ rowCount: steps.length, filters: `detalle:${email}` });
  };

  const byLevel = new Map<string, typeof steps>();
  for (const s of steps) {
    const k = s.levelTitleEs || t('people-learning.dashboardtab.pasos_retirados', 'Pasos ya retirados del contenido');
    byLevel.set(k, [...(byLevel.get(k) ?? []), s]);
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <span>{row?.name ?? email}</span>
            {row && row.status !== 'no_elegible' && (
              <Badge variant={row.category === 'new_clapper' ? 'default' : 'secondary'}>
                {row.category === 'new_clapper' ? t('people_learning.dash.table.mandatory', 'Obligatorio') : t('people_learning.dash.table.optional', 'Opcional')}
              </Badge>
            )}
          </DialogTitle>
          {row && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>{[row.role, row.area, row.country].filter(Boolean).join(' · ')}</span>
              {row.leaderName && <span>{t('people_learning.dash.table.leader', 'Líder')}: {row.leaderName}</span>}
              <span>{t('people_learning.dash.table.start_date', 'Fecha de ingreso')}: {fmtDate(row.startDate)}</span>
              {row.category === 'new_clapper' && <span className={plazoLabel(t, rowPlazo(row, today)).className}>{t('people_learning.dash.table.plazo', 'Plazo')}: {plazoLabel(t, rowPlazo(row, today)).text}</span>}
              <EngagementDot engagement={row.engagement} withLabel />
            </div>
          )}
        </DialogHeader>
        {detail.isLoading ? <WinclapLoader /> : detail.error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('people_learning.dash.detail_error', 'No pude cargar el detalle')}</AlertTitle>
            <AlertDescription>{detail.error.message}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-4 text-sm">
              <div className="rounded-md border p-2.5"><p className="text-xs text-muted-foreground">{t('people_learning.dash.table.status', 'Estado')}</p><p className="font-medium">{row ? statusLabel(t, row.status) : '—'}</p></div>
              <div className="rounded-md border p-2.5"><p className="text-xs text-muted-foreground">{t('people_learning.dash.table.progress', 'Avance')}</p><p className="font-medium">{row ? Math.round(row.progressPct * 100) : 0}% · {done}/{steps.length} {t('people-learning.dashboardtab.pasos', 'pasos')}</p></div>
              <div className="rounded-md border p-2.5"><p className="text-xs text-muted-foreground">{t('people_learning.dash.table.time', 'Tiempo')}</p><p className="font-medium">{fmtMs(p?.elapsedMs ?? 0)}</p></div>
              <div className="rounded-md border p-2.5"><p className="text-xs text-muted-foreground">{t('people_learning.dash.table.points', 'Puntos')}</p><p className="font-medium">{p?.points ?? 0}</p></div>
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span>{t('people-learning.dashboardtab.inicio', 'Inicio')}: {p?.startedAt ? new Date(p.startedAt).toLocaleString(currentLocale()) : '—'}</span>
              <span>{t('people-learning.dashboardtab.fin', 'Fin')}: {p?.finishedAt ? new Date(p.finishedAt).toLocaleString(currentLocale()) : '—'}</span>
              <span>{t('people-learning.dashboardtab.idioma', 'Idioma')}: {p?.lang ?? '—'}</span>
              <span>{t('people-learning.dashboardtab.area_elegida', 'Área elegida')}: {p?.areaChosen ?? '—'}</span>
              {p?.unlockAll && <Badge variant="outline" className="text-amber-700">{t('people-learning.dashboardtab.modo_prueba', 'Modo prueba: todo desbloqueado')}</Badge>}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="h-8" onClick={exportDetail} disabled={steps.length === 0 || logExport.isPending}>
                <Download className="mr-1.5 h-3.5 w-3.5" /> {t('people-learning.dashboardtab.exportar_detalle', 'Exportar detalle (CSV)')}
              </Button>
              {canEdit && (
                <Button
                  size="sm" variant={p?.unlockAll ? 'default' : 'outline'} className="h-8"
                  disabled={setUnlock.isPending}
                  onClick={() => setUnlock.mutate({ email, unlockAll: !p?.unlockAll })}
                  title={t('people-learning.dashboardtab.desbloquear_hint', 'Para pruebas: la persona ve todos los niveles abiertos sin completar los anteriores. Queda registrado.')}
                >
                  <LockOpen className="mr-1.5 h-3.5 w-3.5" />
                  {p?.unlockAll
                    ? t('people-learning.dashboardtab.volver_a_bloquear', 'Volver a bloquear niveles')
                    : t('people-learning.dashboardtab.desbloquear_todo', 'Desbloquear todos los niveles (prueba)')}
                </Button>
              )}
            </div>
            <div className="space-y-3">
              {[...byLevel.entries()].map(([level, list]) => (
                <div key={level}>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{level}</p>
                  <div className="divide-y rounded-md border">
                    {list.map((s) => (
                      <div key={s.stepId} className="flex flex-wrap items-start gap-2 px-3 py-2 text-sm">
                        <span className={`mt-0.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${s.completedAt ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium">
                            {s.titleEs}
                            <span className="ml-1.5 text-xs text-muted-foreground">{s.type === 'video' ? t('people-learning.dashboardtab.tipo_video', 'video') : t('people-learning.dashboardtab.tipo_check', 'system check')}{s.optional ? ` · ${t('people-learning.dashboardtab.bonus', 'bonus')}` : ''}</span>
                          </p>
                          {s.detail?.answers && Object.keys(s.detail.answers).length > 0 && (
                            <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                              {Object.entries(s.detail.answers).map(([k, v]) => (
                                <li key={k}><span className="font-mono">{k}</span>: {fmtAnswer(v)}</li>
                              ))}
                            </ul>
                          )}
                          {s.detail?.note && <p className="mt-1 text-xs text-muted-foreground italic">"{s.detail.note}"</p>}
                        </div>
                        <span className="text-xs tabular-nums text-muted-foreground">{s.completedAt ? new Date(s.completedAt).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' }) : t('people-learning.dashboardtab.pendiente', 'pendiente')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
